import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download, Eye, FolderInput, FolderOpen, Laptop, Link2, Pencil, RotateCcw, Shield, Star, StarOff, Trash2, UserPlus, XCircle,
} from 'lucide-react';
import driveApi, { errorMessage, ROLES } from '../../api/drive';
import { useDrive } from '../../contexts/DriveProvider';
import { formatSize, previewType } from '../../lib/files';
import NameDialog from './NameDialog';
import ShareDialog from './ShareDialog';
import SecurityDialog from './SecurityDialog';
import PreviewDialog from './PreviewDialog';
import ConfirmDialog from './ConfirmDialog';
import MoveDialog from './MoveDialog';

export const startDownload = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 * Everything you can do to an item, plus the dialogs those actions open.
 * Render `dialogs` once in the page.
 */
const useItemActions = ({ trashMode = false } = {}) => {
  const navigate = useNavigate();
  const { bump, toast, hasDesktop } = useDrive();
  const [renaming, setRenaming] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [sharingOut, setSharingOut] = useState(false);
  const [securing, setSecuring] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [moving, setMoving] = useState(null);
  const [purging, setPurging] = useState(null);
  const [zipping, setZipping] = useState(null); // {items, label, summary}

  const run = async (fn, success, failure) => {
    try {
      await fn();
      if (success) toast(success);
      bump();
    } catch (err) {
      toast(errorMessage(err, failure), 'error');
    }
  };

  const download = async (items) => {
    const list = Array.isArray(items) ? items : [items];
    try {
      const url = await driveApi.downloadUrl(list.map((i) => i.id));
      const zipping = list.length > 1 || list[0].kind === 'folder';
      if (zipping) toast('Preparing your .zip…');
      startDownload(url);
    } catch (err) {
      toast(errorMessage(err, 'Download failed.'), 'error');
    }
  };

  // Everything in a folder or drive can be huge: show what's coming before the .zip starts
  const downloadAll = async (items, label) => {
    if (!items.length) return;
    setZipping({ items, label, summary: null });
    try {
      const summary = await driveApi.downloadSummary(items.map((i) => i.id));
      setZipping((z) => (z && z.items === items ? { ...z, summary } : z));
    } catch {
      setZipping((z) => (z && z.items === items ? { ...z, summary: { error: true } } : z));
    }
  };

  const zipSummary = zipping?.summary;
  const zipDescription = !zipSummary
    ? 'Counting files…'
    : zipSummary.error
      ? 'Everything in it is downloaded as one .zip, which can take a long time for big folders.'
      : zipSummary.files === 0
        ? 'There are no files to download here.'
        : `That's ${zipSummary.files.toLocaleString()} ${zipSummary.files === 1 ? 'file' : 'files'} (${formatSize(zipSummary.bytes)}) in one .zip. The server packs everything before your download starts, so big folders can take a long time. To get just a few files, select them first.`;

  const open = (item) => {
    if (trashMode) return;
    if (item.kind === 'folder') navigate(`/drive/${item.drive}/folder/${item.id}`);
    else if (previewType(item)) setPreviewing(item);
    else download(item);
  };

  const trash = (items) => {
    const list = Array.isArray(items) ? items : [items];
    return run(
      () => Promise.all(list.map((i) => driveApi.trash(i.id))),
      list.length > 1 ? `${list.length} items moved to trash` : `“${list[0].name}” moved to trash`,
      'Could not move to trash.',
    );
  };

  const restore = (items) => {
    const list = Array.isArray(items) ? items : [items];
    return run(
      () => Promise.all(list.map((i) => driveApi.restore(i.id))),
      list.length > 1 ? `${list.length} items restored` : `“${list[0].name}” restored`,
      'Could not restore.',
    );
  };

  // purging: { items: [...] } for a selection, or { all: true } to empty the trash
  const purge = (items) => setPurging({ items: Array.isArray(items) ? items : [items] });
  const emptyTrash = () => setPurging({ all: true });
  const confirmPurge = async () => {
    try {
      const res = await driveApi.emptyTrash(purging.all ? null : purging.items.map((i) => i.id));
      const done = res.deleted === 1 ? '1 item deleted forever' : `${res.deleted} items deleted forever`;
      toast(res.skipped ? `${done}. ${res.skipped} kept: only a manager of that folder can delete it forever.` : done);
      bump();
    } catch (err) {
      toast(errorMessage(err, 'Could not delete.'), 'error');
    }
  };
  const purgeCount = purging?.items?.length;
  const purgeTitle = purging?.all
    ? 'Empty the trash?'
    : purgeCount === 1 ? `Delete “${purging.items[0].name}” forever?` : `Delete ${purgeCount} items forever?`;

  const toggleStar = (item) => run(
    () => (item.starred ? driveApi.unstar(item.id) : driveApi.star(item.id)),
    null,
    'Could not update star.',
  );

  const moveInto = (ids, folder) => run(
    () => Promise.all(ids.map((id) => driveApi.move(id, folder.id, folder.drive))),
    `Moved to “${folder.name}”`,
    'Could not move.',
  );

  const menu = (item) => {
    if (trashMode) {
      const entries = [];
      if (item.role >= ROLES.EDITOR) entries.push({ label: 'Restore', icon: RotateCcw, run: () => restore(item) });
      if (item.role >= ROLES.MANAGER) entries.push({ label: 'Delete forever', icon: XCircle, danger: true, run: () => purge(item) });
      return entries;
    }
    const entries = [];
    if (item.kind === 'folder') entries.push({ label: 'Open', icon: FolderOpen, run: () => open(item) });
    else if (previewType(item)) entries.push({ label: 'Preview', icon: Eye, run: () => setPreviewing(item) });
    if (item.kind !== 'folder' && hasDesktop) {
      // Hands the file to the desktop app: it opens a temporary copy whose edits upload back
      entries.push({
        label: 'Open on this computer',
        icon: Laptop,
        run: () => {
          window.location.href = `lamptwist-drive://open?node=${item.id}`;
          toast(`Opening “${item.name}” in the desktop app…`);
        },
      });
    }
    entries.push({ label: item.kind === 'folder' ? 'Download as .zip' : 'Download', icon: Download, run: () => download(item) });
    entries.push('-');
    entries.push({ label: 'Share', icon: UserPlus, run: () => { setSharingOut(false); setSharing(item); } });
    if (!item.no_external) {
      entries.push({ label: 'Share outside LampTwist', icon: Link2, run: () => { setSharingOut(true); setSharing(item); } });
    }
    if (item.role >= ROLES.EDITOR) {
      entries.push({ label: 'Rename', icon: Pencil, run: () => setRenaming(item) });
      entries.push({ label: 'Move to…', icon: FolderInput, run: () => setMoving([item]) });
    }
    entries.push({ label: item.starred ? 'Remove star' : 'Add star', icon: item.starred ? StarOff : Star, run: () => toggleStar(item) });
    if (item.kind === 'folder' && item.role >= ROLES.MANAGER) {
      entries.push({ label: 'Folder security', icon: Shield, run: () => setSecuring(item) });
    }
    if (item.role >= ROLES.EDITOR) {
      entries.push('-');
      entries.push({ label: 'Move to trash', icon: Trash2, danger: true, run: () => trash(item) });
    }
    return entries;
  };

  const dialogs = (
    <>
      <NameDialog
        open={!!renaming}
        onOpenChange={(o) => !o && setRenaming(null)}
        title="Rename"
        initial={renaming?.name || ''}
        confirmLabel="Rename"
        onSubmit={async (name) => {
          try {
            await driveApi.rename(renaming.id, name);
            bump();
            return true;
          } catch (err) {
            toast(errorMessage(err, 'Could not rename.'), 'error');
            return false;
          }
        }}
      />
      <ShareDialog item={sharing} focusGuests={sharingOut} onOpenChange={(o) => !o && setSharing(null)} />
      <SecurityDialog item={securing} onOpenChange={(o) => !o && setSecuring(null)} />
      <MoveDialog items={moving} onOpenChange={(o) => !o && setMoving(null)} />
      <PreviewDialog item={previewing} onOpenChange={(o) => !o && setPreviewing(null)} onDownload={download} />
      <ConfirmDialog
        open={!!zipping}
        onOpenChange={(o) => !o && setZipping(null)}
        title={`Download everything in “${zipping?.label || ''}”?`}
        description={zipDescription}
        confirmLabel="Download .zip"
        destructive={false}
        disabled={!zipSummary || zipSummary.files === 0}
        onConfirm={() => download(zipping.items)}
      />
      <ConfirmDialog
        open={!!purging}
        onOpenChange={(o) => !o && setPurging(null)}
        title={purgeTitle}
        description={purging?.all
          ? "Everything in your trash that you're allowed to delete is removed for everyone, including older versions. It can't be undone."
          : "This removes it and everything inside it for everyone, including older versions. It can't be undone."}
        confirmLabel={purging?.all ? 'Empty trash' : 'Delete forever'}
        onConfirm={confirmPurge}
      />
    </>
  );

  return {
    open, download, downloadAll, trash, restore, purge, emptyTrash, toggleStar, moveInto, menu, dialogs,
    share: setSharing, rename: setRenaming, moveTo: (items) => setMoving(Array.isArray(items) ? items : [items]), secure: setSecuring, preview: setPreviewing,
  };
};

export default useItemActions;
